/**
 * Creates a silent audio track of a specified duration.
 * @param durationInSeconds The desired duration of the silent audio in seconds.
 * @returns A Base64 encoded data URL for the silent WAV audio file.
 */
export function createSilentAudio(durationInSeconds: number): string {
  const sampleRate = 44100;
  const numChannels = 1;
  const numFrames = Math.floor(durationInSeconds * sampleRate);
  const dataSize = numFrames * numChannels * 2; // 16-bit audio

  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);

  // RIFF header
  writeString(view, 0, 'RIFF');
  view.setUint32(4, 36 + dataSize, true);
  writeString(view, 8, 'WAVE');

  // fmt chunk
  writeString(view, 12, 'fmt ');
  view.setUint32(16, 16, true); // Sub-chunk size
  view.setUint16(20, 1, true); // Audio format (1 for PCM)
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * numChannels * 2, true); // Byte rate
  view.setUint16(32, numChannels * 2, true); // Block align
  view.setUint16(34, 16, true); // Bits per sample

  // data chunk
  writeString(view, 36, 'data');
  view.setUint32(40, dataSize, true);

  // Audio data (silence) is already all zeros, so we don't need to write it.

  const audioBlob = new Blob([view], { type: 'audio/wav' });
  return URL.createObjectURL(audioBlob);
}

function writeString(view: DataView, offset: number, str: string) {
  for (let i = 0; i < str.length; i++) {
    view.setUint8(offset + i, str.charCodeAt(i));
  }
}
